import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from "react";
import useMachineStore from "../../Store/MachineStore";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    Select
} from "@mui/material";
import Box from "@mui/material/Box";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import MenuItem from "@mui/material/MenuItem";
import {Close} from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import {TaskItem} from "./TaskItem";
import {removeFromHistory, updateTaskPosition} from "../../Service/TaskService";
import {removeTasks, transferTasks} from "../../Service/MachineService";
import CircularProgress from "@mui/material/CircularProgress";
import {InView, useInView} from "react-intersection-observer";
import Typography from "@mui/material/Typography";
import {buildPreviewTasks, toggleId} from "./previewTasks";
import {isSameTaskDisplay} from "./taskItemEquality";

// Une file peut compter 223 tâches : les cartes arrivent par paquets au fil du défilement au lieu d'être toutes rendues à l'ouverture.
const CARDS_PER_BATCH = 24;
const NO_IDS = [];
const NO_TASKS = [];
const noop = () => {};

const arePreviewCardPropsEqual = (previous, next) => {
    return isSameTaskDisplay(previous.task, next.task)
        && previous.selected === next.selected
        && previous.showMachine === next.showMachine
        && previous.toDeletedTasks === next.toDeletedTasks
        && previous.onToggle === next.onToggle
        && previous.onSetFirstPosition === next.onSetFirstPosition
        && previous.onTaskSended === next.onTaskSended
        && previous.onRemoveTask === next.onRemoveTask
}

const PreviewTaskCard = memo(function PreviewTaskCard(
    {task, selected, showMachine, toDeletedTasks, onToggle, onSetFirstPosition, onTaskSended, onRemoveTask}
) {
    return (
        <Grid2 xs={3} sx={{contentVisibility: "auto", containIntrinsicSize: "auto 320px"}}>
            <Box
                className={"preview-task " + (selected ? "active" : "")}
                sx={{borderRadius: 2}}
                onClick={() => onToggle(task.id)}
            >
                <Box sx={{
                    height: 250,
                    borderRadius: 2,
                    background: "#eeee",
                    position: "relative",
                    overflow: "hidden"
                }}>
                    <img
                        alt={task.fileName || ""}
                        src={task.previewUrl}
                        loading="lazy"
                        decoding="async"
                        style={{width: "100%", height: "100%", objectFit: "contain", display: "block"}}
                    />
                </Box>
                <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
                    <TaskItem
                        showMachine={showMachine}
                        task={task}
                        toDeletedTasks={toDeletedTasks}
                        onSetFirstPosition={onSetFirstPosition}
                        onPreviewFileTask={noop}
                        onTaskSended={onTaskSended}
                        onRemoveTask={onRemoveTask}
                    />
                </Stack>
            </Box>
        </Grid2>
    )
}, arePreviewCardPropsEqual)

export default function MachinePreviewTasks({open, setOpen, machine, onLoadNext, tasks = null, hasMore = false}) {
    const machines = useMachineStore(state => state.machines)
    const [currentMachineId, setCurrentMachineId] = useState(machine ? machine.id : null)
    const [selectedTasks, setSelectedTasks] = useState(NO_IDS)
    const [toDeletedTasks, setToDeleteTasks] = useState(NO_IDS)
    const [visibleCount, setVisibleCount] = useState(CARDS_PER_BATCH)
    const {ref: moreCardsRef, inView: moreCardsInView} = useInView({rootMargin: "400px 0px"})

    const isHistory = tasks !== null

    // Les actions des cartes restent les mêmes fonctions d'un rendu à l'autre (sinon toutes les cartes se redessinent
    // à chaque sélection) : elles lisent la sélection et la machine courantes par ces références.
    const selectedTasksRef = useRef(selectedTasks)
    selectedTasksRef.current = selectedTasks
    const machineRef = useRef(machine)
    machineRef.current = machine

    useEffect(() => {
        setCurrentMachineId(machine ? machine.id : null)
    }, [machine]);

    useEffect(() => {
        setVisibleCount(CARDS_PER_BATCH)
    }, [currentMachineId, isHistory]);

    const currentTasks = useMemo(() => {
        if (!open) {
            return NO_TASKS
        }

        return isHistory ? tasks : buildPreviewTasks(machines, currentMachineId)
    }, [open, isHistory, tasks, machines, currentMachineId])

    const visibleTasks = useMemo(() => currentTasks.slice(0, visibleCount), [currentTasks, visibleCount])
    const hasMoreCards = visibleCount < currentTasks.length

    useEffect(() => {
        if (moreCardsInView && hasMoreCards) {
            setVisibleCount(count => count + CARDS_PER_BATCH)
        }
    }, [moreCardsInView, hasMoreCards, visibleCount]);

    const handleToggle = useCallback((taskId) => {
        setSelectedTasks(previous => toggleId(previous, taskId))
    }, [])

    const handleTaskSend = useCallback((_task, _machine, position = "last") => {
        const selected = selectedTasksRef.current
        if (selected.length > 0 && selected.includes(_task.id)) {
            transferTasks(selected, position, _machine).then(() => {
                setOpen(false)
            })
        } else {
            updateTaskPosition(_task, position, _machine).then(() => {
                setOpen(false)
            })
        }
    }, [setOpen])

    const handleSetFirstPosition = useCallback((task) => {
        setOpen(false)
        handleTaskSend(task, machineRef.current, "first")
    }, [setOpen, handleTaskSend])

    const handleRemoveTask = useCallback((task) => {
        const userConfirmed = window.confirm('Voulez-vous vraiment supprimer ?');
        if (userConfirmed !== true) {
            return
        }

        const selected = selectedTasksRef.current
        if (selected.length > 1) {
            setToDeleteTasks(selected)
            removeTasks(selected).then()
        } else {
            setToDeleteTasks([task.id])
            removeFromHistory({
                taskId: task.id,
                status: 4,
                folder: "history"
            }).then()
        }
    }, [])

    const titleMachine = isHistory ? machine : machines.find(item => item.id === currentMachineId)
    const scopeLabel = titleMachine ? `${titleMachine.number} - ${titleMachine.name}` : "toutes les machines"
    const showMachine = currentMachineId === null

    // Seul l'historique d'une machine est limité à 5 jours (TaskRepository::getTaskHistories), pas l'historique global.
    const getEmptyMessage = () => {
        if (!isHistory) {
            return "Aucune tâche en cours"
        }

        return machine
            ? `Aucune impression terminée sur la ${machine.number} ces 5 derniers jours`
            : "Aucune impression terminée"
    }

    return (
        <>
            <Dialog
                fullWidth
                open={open}
                onClose={setOpen}
                maxWidth="lg"
            >
                <DialogTitle sx={{pr: 7}}>
                    {isHistory ? "Historique" : "Tâches en cours"} · {scopeLabel}
                    {isHistory && machine &&
                        <Typography component="span" variant="body2" color="text.secondary" sx={{display: "block"}}>
                            Impressions terminées ces 5 derniers jours
                        </Typography>
                    }
                </DialogTitle>
                <IconButton
                    aria-label="close"
                    onClick={() => {
                        setOpen(false)
                    }}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                    }}
                >
                    <Close/>
                </IconButton>
                <DialogContent>
                    <Box>
                        {!isHistory &&
                            <Grid2 container spacing={2} sx={{mb: 1}}>
                                <Grid2 xs={3}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Machines</InputLabel>
                                        <Select
                                            size="small"
                                            label="Machines"
                                            onChange={(e) => {
                                                setCurrentMachineId(e.target.value === "" ? null : +e.target.value)
                                            }}
                                            defaultValue={machine?.id}
                                        >
                                            <MenuItem value="">Toutes</MenuItem>
                                            {machines.map((_machine) => (
                                                <MenuItem key={_machine.id}
                                                          value={_machine.id}>
                                                    {_machine.number} - {_machine.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid2>
                            </Grid2>
                        }

                        <Grid2 container spacing={2}>
                            {currentTasks.length === 0 &&
                                <Alert sx={{mt: 2, textAlign: "center", width: "100%"}} severity="info">
                                    {getEmptyMessage()}
                                </Alert>}

                            {visibleTasks.map(task => (
                                <PreviewTaskCard
                                    key={task.id}
                                    task={task}
                                    selected={selectedTasks.includes(task.id)}
                                    showMachine={showMachine}
                                    toDeletedTasks={toDeletedTasks}
                                    onToggle={handleToggle}
                                    onSetFirstPosition={handleSetFirstPosition}
                                    onTaskSended={handleTaskSend}
                                    onRemoveTask={handleRemoveTask}
                                />
                            ))}
                        </Grid2>

                        {hasMoreCards &&
                            <Box ref={moreCardsRef} sx={{textAlign: "center", mt: 2}}>
                                <CircularProgress size={28}/>
                            </Box>
                        }

                        {!hasMoreCards && isHistory && hasMore && tasks.length > 0 &&
                            <InView onChange={(inView) => {
                                if (inView && onLoadNext) {
                                    onLoadNext()
                                }
                            }}>
                                <Box sx={{textAlign: "center", mt: 2}}>
                                    <CircularProgress/>
                                </Box>
                            </InView>
                        }
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setOpen(false)
                    }}>Fermer</Button>
                </DialogActions>
            </Dialog>
        </>
    )
}
